import { Box } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  YAxisTick,
  XAxisTick,
  renderLegend,
  renderLoadingLegend,
} from "../../../../../../../components/Graphs";
import { COLORS } from "@asayinc/component-library";
import { NetworkTrendGraphTooltip } from "../NetworkTrendGraphCard/components";

interface INetworkGraphData {
  name: string;
  shareholders?: number | null;
  shares?: number | null;
}

interface IProps {
  graphData: INetworkGraphData[];
  shareholderMax: number;
  sharesMax: number;
  isLoading: boolean;
}

const NetworkTrendGraph = ({
  graphData,
  shareholderMax,
  sharesMax,
  isLoading,
}: IProps) => {
  // Create evenly spaced ticks rounded to whole numbers for clean display
  // This maintains alignment while avoiding decimals
  const tickCount = 4;
  const shareholderTicks = Array(tickCount)
    .fill(0)
    .map((_, i) => Math.round((i * shareholderMax) / (tickCount - 1)));

  const sharesTicks = Array(tickCount)
    .fill(0)
    .map((_, i) => Math.round((i * sharesMax) / (tickCount - 1)));

  return (
    <Box
      height="352px"
      sx={{ svg: { overflow: "visible" } }}
      data-testid="network-trend-graph"
    >
      <ResponsiveContainer width="100%" height="95%">
        <LineChart
          data={graphData}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
        >
          <Legend
            iconType="circle"
            iconSize={isLoading ? 0 : 8}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            align="right"
            height={50}
            payload={[
              {
                value: "Shares",
                type: "circle",
                id: "shares",
                color: COLORS.DATA_VIZ_DARK_GREEN,
              },
              {
                value: "Shareholders",
                type: "circle",
                id: "shareholders",
                color: COLORS.DATA_VIZ_LIGHT_GREEN,
              },
            ]}
            formatter={isLoading ? renderLoadingLegend : renderLegend}
            verticalAlign="top"
          />
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            padding={{ left: 20, right: 20 }}
            axisLine={false}
            tickLine={false}
            tick={<XAxisTick isLoading={isLoading} />}
            domain={isLoading ? [1, 6] : undefined}
            ticks={isLoading ? [1, 2, 3, 4, 5, 6] : undefined}
            type={isLoading ? "number" : "category"}
            tickCount={isLoading ? 6 : undefined}
            interval={isLoading ? undefined : Math.floor(graphData.length / 6)}
            tickMargin={15}
          />
          <YAxis
            yAxisId="left"
            axisLine={false}
            tick={
              <YAxisTick
                color={COLORS.DATA_VIZ_DARK_GREEN}
                anchor="end"
                isLoading={isLoading}
              />
            }
            tickLine={false}
            tickMargin={12}
            domain={isLoading ? [0, 4] : [0, Math.max(...sharesTicks)]}
            ticks={isLoading ? [1, 2, 3, 4] : sharesTicks}
            width={50}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickMargin={12}
            type="number"
            tickCount={4}
            allowDataOverflow
            ticks={isLoading ? [1, 2, 3, 4] : shareholderTicks}
            tickLine={false}
            tick={
              <YAxisTick
                color={COLORS.DATA_VIZ_LIGHT_GREEN}
                anchor="start"
                isLoading={isLoading}
                rightTick
              />
            }
            axisLine={false}
            domain={isLoading ? [0, 4] : [0, Math.max(...shareholderTicks)]}
          />
          <Tooltip content={<NetworkTrendGraphTooltip />} />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="shares"
            stroke={COLORS.DATA_VIZ_DARK_GREEN}
            strokeWidth={3}
            dot={false}
            connectNulls={true}
          />
          <Line
            yAxisId="right"
            strokeWidth={3}
            dot={false}
            type="monotone"
            dataKey="shareholders"
            stroke={COLORS.DATA_VIZ_LIGHT_GREEN}
            connectNulls={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default NetworkTrendGraph;
